import React from 'react';
import {
  Bot, MessageSquare, Volume2, Mic, MicOff, PhoneCall, PhoneOff, Zap,
} from 'lucide-react';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const ChatBubble = ({ msg }) => {
  if (msg.role === 'system') {
    return <span className="text-gray-500 text-xs">{msg.text}</span>;
  }
  const isUser = msg.role === 'user';
  return (
    <div className={`max-w-[80%] rounded-lg px-4 py-2 ${isUser ? 'bg-teal-400 text-black' : 'bg-[#1a1a1f] text-white'}`}>
      {!isUser && (
        <div className="flex items-center gap-1 mb-1">
          <Volume2 className="w-3 h-3" />
          <span className="text-xs opacity-70">AI Speaking</span>
        </div>
      )}
      <p className="text-sm">{msg.text}</p>
    </div>
  );
};

export const TestCallModal = ({
  agent,
  isCallActive,
  callDuration,
  phoneNumber,
  chatMessages,
  chatInput,
  isMuted,
  minutesRemaining,
  onPhoneNumberChange,
  onChatInputChange,
  onSendMessage,
  onToggleCall,
  onToggleMute,
  onClose,
  onUpgrade,
}) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="test-call-modal">
    <div className="bg-[#0d1117] border border-gray-800 rounded-xl w-full max-w-md overflow-hidden">
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold">{agent.name}</p>
              <p className="text-white/70 text-sm">
                {isCallActive ? `In call - ${formatDuration(callDuration)}` : 'Demo Call'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-xl"
            data-testid="test-call-close-btn"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {!isCallActive && (
        <div className="p-4 border-b border-gray-800">
          <label className="block text-gray-300 text-sm mb-2" htmlFor="test-call-phone-input">
            Phone Number to Call
          </label>
          <input
            id="test-call-phone-input"
            type="tel"
            value={phoneNumber}
            onChange={(e) => onPhoneNumberChange(e.target.value)}
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            placeholder="+1 (555) 123-4567"
            data-testid="phone-number-input"
          />
          <p className="text-gray-500 text-xs mt-2">
            <span className="text-yellow-400">Demo Mode:</span> Calls are simulated. Real calling requires Telnyx API key.
          </p>
        </div>
      )}

      <div className="h-64 overflow-y-auto p-4 space-y-3">
        {chatMessages.map((msg, idx) => (
          <div
            key={`${msg.role}-${idx}-${msg.text?.slice(0, 12)}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <ChatBubble msg={msg} />
          </div>
        ))}
      </div>

      <form onSubmit={onSendMessage} className="border-t border-gray-800 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => onChatInputChange(e.target.value)}
            className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            placeholder="Type a message or use voice..."
            disabled={!isCallActive}
            data-testid="chat-input"
          />
          <button
            type="submit"
            className="bg-teal-400 hover:bg-teal-500 text-black p-2 rounded-lg transition-colors disabled:opacity-50"
            disabled={!isCallActive}
            data-testid="chat-send-btn"
          >
            <MessageSquare className="w-5 h-5" />
          </button>
        </div>
      </form>

      <div className="border-t border-gray-800 p-4 flex items-center justify-center gap-4">
        <button
          onClick={onToggleMute}
          disabled={!isCallActive}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            isMuted ? 'bg-red-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          } disabled:opacity-50`}
          data-testid="mute-toggle-btn"
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button
          onClick={onToggleCall}
          disabled={!isCallActive && !phoneNumber}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
            isCallActive ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
          } disabled:opacity-50`}
          data-testid="call-toggle-btn"
        >
          {isCallActive ? <PhoneOff className="w-6 h-6" /> : <PhoneCall className="w-6 h-6" />}
        </button>
        <button
          className="w-12 h-12 rounded-full bg-gray-700 text-gray-300 hover:bg-gray-600 flex items-center justify-center transition-colors"
          data-testid="speaker-btn"
          aria-label="Speaker"
        >
          <Volume2 className="w-5 h-5" />
        </button>
      </div>

      {minutesRemaining <= 5 && (
        <div className="bg-yellow-400/10 border-t border-yellow-400/30 p-3 text-center">
          <p className="text-yellow-400 text-sm">
            <Zap className="w-4 h-4 inline mr-1" />
            Low minutes! Only {minutesRemaining} min remaining.
            <button onClick={onUpgrade} className="underline ml-1" data-testid="upgrade-from-modal-btn">
              Upgrade
            </button>
          </p>
        </div>
      )}
    </div>
  </div>
);
